import React, { useState } from 'react';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportProduct,
  ProductEntityLink,
} from 'api/ExportHubAPI';
import { RoleChip, RiskFlags, computeEffectiveDestinations } from './entityPresets';
import LinkEntityModal from './LinkEntityModal';

interface Props {
  product: ExportProduct;
  groupId: string | undefined;
  entities: ProductEntityLink[];
  onChanged: () => void;
}

/**
 * Customers & Consignees (spec §4.7). Links entities to the product with a role,
 * and surfaces the product's effective destinations — the union of linked
 * Customer/Consignee/End-User countries and manually entered destinations, each
 * tagged with its source.
 */
const CustomersConsignees: React.FC<Props> = ({
  product,
  groupId,
  entities,
  onChanged,
}) => {
  const [linkOpen, setLinkOpen] = useState(false);

  const effective = computeEffectiveDestinations(
    product.intended_destination_countries || [],
    entities,
  );

  const unlink = async (e: ProductEntityLink) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.unlinkEntity(product.id, e.id, groupId);
      enqueueSnackbar('Entity unlinked.', { variant: 'success' });
      onChanged();
    } catch (err) {
      enqueueSnackbar('Failed to unlink entity.', { variant: 'error' });
    }
  };

  return (
    <>
      <div className='eh-regime-subhead'>
        <span>Linked parties</span>
        <button
          className='eh-mini-btn'
          type='button'
          onClick={() => setLinkOpen(true)}
        >
          <PersonAddAltOutlinedIcon style={{ fontSize: 15 }} /> Link Entity
        </button>
      </div>

      {entities.length === 0 && (
        <div className='eh-detail-note'>
          No customers or consignees linked yet.
        </div>
      )}

      {entities.map((e) => (
        <div className='eh-entity-block' key={e.id}>
          <div className='eh-entity-row'>
            <div className='eh-entity-info'>
              <span className='eh-entity-name'>
                {e.name}
                <RoleChip value={e.role} />
              </span>
              <span className='eh-entity-meta'>
                {e.country || '—'}
                {e.registration_id ? ` · ${e.registration_id}` : ''}
                <RiskFlags flags={e.risk_flags} />
              </span>
              {e.link_notes && (
                <span className='eh-entity-notes'>{e.link_notes}</span>
              )}
            </div>
            <div className='eh-entity-actions'>
              <button
                className='eh-linked-x'
                type='button'
                onClick={() => unlink(e)}
                title='Unlink entity'
                aria-label='Unlink entity'
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            </div>
          </div>
        </div>
      ))}

      <div className='eh-eff-dest'>
        <div className='eh-regime-subhead eh-eff-dest-head'>
          <span>
            <PublicOutlinedIcon
              style={{ fontSize: 14, verticalAlign: '-2px', marginRight: 5 }}
            />
            Effective destinations
          </span>
        </div>
        {effective.length === 0 ? (
          <div className='eh-detail-note'>
            No destinations yet. Add intended destinations or link a
            customer/consignee/end user.
          </div>
        ) : (
          effective.map((d) => (
            <div className='eh-eff-dest-row' key={d.country}>
              <span className='eh-eff-dest-country'>{d.country}</span>
              <span className='eh-eff-dest-source'>{d.sources.join(' · ')}</span>
            </div>
          ))
        )}
      </div>

      <LinkEntityModal
        open={linkOpen}
        productId={product.id}
        groupId={groupId}
        linkedIds={entities.map((e) => e.id)}
        onClose={() => setLinkOpen(false)}
        onLinked={() => {
          setLinkOpen(false);
          onChanged();
        }}
      />
    </>
  );
};

export default CustomersConsignees;
